import {applyAutoPlacementResults, buildDesignatorToUidMap} from "@buildwithflux/core";
import {AnyPcbBakedNode, AutoPlacementApi, AutoPlacementJob, PcbNodesMap} from "@buildwithflux/models";
import {AutoPlacementJobRepository} from "@buildwithflux/repositories";
import {create} from "zustand";
import {AppStore} from "modules/app/state/app";
import {CurrentUserService, FluxAuthClaimsService} from "modules/auth";
import {AutoLayoutClientJobState} from "modules/auto_layout/services/AutoLayoutStoreService";
import type {DocumentStore} from "modules/document/state/document";
import {AbstractDocumentPreviewService} from "modules/preview/DocumentPreviewService";

export interface AutoPlacementIteration {
    iterationIndex: number;
    loaded: boolean;
}

export interface AutoPlacementState {
    state: AutoLayoutClientJobState;
    iterations: AutoPlacementIteration[];
    loadingIteration: boolean;
    currentJob: AutoPlacementJob | null;
    sliderPosition: number;
}

function convertState(state: string): AutoLayoutClientJobState | null {
    switch (state) {
        case "running":
            return AutoLayoutClientJobState.running;
        case "cancelled":
            return AutoLayoutClientJobState.canceled;
        case "paused":
            return AutoLayoutClientJobState.paused;
        case "complete":
            return AutoLayoutClientJobState.complete;
    }
    return null;
}

interface AutoPlacementStateStoreType {
    autoPlacementState: AutoPlacementState | null;
    setAutoPlacementState: (state: AutoPlacementState | null) => void;

    updateFromJob: (job: AutoPlacementJob | null) => void;
    clear: () => void;
    setError: () => void;
    setSliderPosition: (position: number) => void;
    initialize: () => void;
    setJobState: (state: AutoLayoutClientJobState) => void;
}

export class AutoPlacementService {
    useAutoPlacementStateStore = create<AutoPlacementStateStoreType>((set) => ({
        autoPlacementState: null,
        setAutoPlacementState: (state: AutoPlacementState | null) => set(() => ({autoPlacementState: state})),
        updateFromJob: (job: AutoPlacementJob | null) => {
            set((prev) => ({
                autoPlacementState: job
                    ? {
                          state: convertState(job.jobStatus) ?? AutoLayoutClientJobState.error,
                          iterations: job.iterations.map((it) => ({
                              iterationIndex: it.iterationIndex,
                              loaded: true,
                          })),
                          loadingIteration: false,
                          currentJob: job,
                          sliderPosition: prev.autoPlacementState?.sliderPosition ?? job.iterations.length - 1,
                      }
                    : null,
            }));
        },
        clear: () => set(() => ({autoPlacementState: null})),
        setError: () =>
            set(() => ({
                autoPlacementState: {
                    state: AutoLayoutClientJobState.error,
                    iterations: [],
                    loadingIteration: false,
                    currentJob: null,
                    sliderPosition: 0,
                },
            })),
        setSliderPosition: (position: number) =>
            set((prev) => ({
                autoPlacementState: prev.autoPlacementState
                    ? {...prev.autoPlacementState, sliderPosition: position}
                    : null,
            })),
        initialize: () =>
            set(() => ({
                autoPlacementState: {
                    state: AutoLayoutClientJobState.waiting,
                    iterations: [],
                    loadingIteration: false,
                    currentJob: null,
                    sliderPosition: 0,
                },
            })),
        setJobState: (state: AutoLayoutClientJobState) =>
            set((prev) => ({
                autoPlacementState: prev.autoPlacementState ? {...prev.autoPlacementState, state} : null,
            })),
    }));

    unsubscribeJobState: (() => void) | null = null;

    constructor(
        private readonly autoPlacementJobRepository: AutoPlacementJobRepository,
        private readonly currentUserService: CurrentUserService,
        private readonly fluxAuthService: FluxAuthClaimsService,
        private readonly documentPreviewService: AbstractDocumentPreviewService,
        private readonly appStore: AppStore,
        private readonly documentStore: DocumentStore,
    ) {}

    async initialize() {
        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        this.useAutoPlacementStateStore.getState().clear();

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        // Try to load an existing active job, if exists then subscribe to it
        const activeJob = await this.autoPlacementJobRepository.getActiveJobForProject(
            currentUser.handle,
            currentUser.uid,
            documentUid,
        );
        if (activeJob) {
            this.useAutoPlacementStateStore.getState().updateFromJob(activeJob);
            this.subscribeToJobState(activeJob.jobUid);
        }
    }

    async subscribeToJobState(jobUid: AutoPlacementJob["jobUid"]) {
        this.unsubscribeJobState?.();
        this.unsubscribeJobState = null;

        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        this.unsubscribeJobState = this.autoPlacementJobRepository.subscribe(currentUser.handle, jobUid, (change) => {
            // eslint-disable-next-line no-console
            console.log("PLACEMENT firebase update:", change);

            if (change.type === "updated" && change.data.active) {
                this.useAutoPlacementStateStore.getState().updateFromJob(change.data);
                this.showLatestPreview(change.data);
            } else {
                this.cleanup();
            }
        });
    }

    cleanup() {
        this.unsubscribeJobState?.();
        this.unsubscribeJobState = null;
        this.useAutoPlacementStateStore.getState().clear();
        this.documentPreviewService.clearPreview();
    }

    async showLatestPreview(activeJob: AutoPlacementJob, targetIterationIndex?: number) {
        if (targetIterationIndex === undefined) {
            targetIterationIndex = activeJob.iterations[activeJob.iterations.length - 1]?.iterationIndex;
        }

        try {
            const documentPcbNodes = this.documentStore.getState().document?.pcbLayoutNodes;
            if (!documentPcbNodes) return;
            const designatorsToUids = buildDesignatorToUidMap(documentPcbNodes);

            // const previewUid =
            //     activeJob.jobUid +
            //     "__" +
            //     activeJob.iterations
            //         .slice(0, (targetIterationIndex ?? activeJob.iterations.length - 1) + 1)
            //         .map((it) => it.iterationIndex)
            //         .join("-");

            // await this.documentPreviewService.clearPreview();
            await this.documentPreviewService.renderPreview((draft) => {
                // TODO: pre-accumulate iterations
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                activeJob.iterations.forEach((it) => {
                    if (it.iterationIndex > (targetIterationIndex ?? Infinity)) return;

                    const toApply = applyAutoPlacementResults(it, draft.pcbLayoutNodes, designatorsToUids);

                    // TODO: pass iteration uid for diff performance
                    const toApplyUids = Object.keys(toApply);
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    toApplyUids.forEach((nodeUid) => {
                        const node = draft.pcbLayoutNodes?.[nodeUid];
                        const toApplyNode = toApply[nodeUid];
                        if (!node || !toApplyNode) return;
                        node.pcbNodeRuleSet = {
                            ...node.pcbNodeRuleSet,
                            ...toApplyNode.updatedPcbLayoutRules,
                        };
                    });
                });
            });
            // }, previewUid);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(
                "PLACEMENT error:",
                "Some components do not have unique designators, cannot load auto-placement.",
            );
        }
    }

    async run(pcbNodes: PcbNodesMap<AnyPcbBakedNode>) {
        // sanity check
        try {
            buildDesignatorToUidMap(pcbNodes);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(
                "PLACEMENT error:",
                "Some components do not have unique designators, cannot run auto-placement.",
            );

            this.useAutoPlacementStateStore.getState().setError();
            return;
        }

        const user = this.currentUserService.getCurrentUser();
        if (!user) {
            this.useAutoPlacementStateStore.getState().setError();
            return;
        }

        const documentState = this.documentStore.getState().document;
        const elements = documentState?.elements;
        if (!elements) {
            this.useAutoPlacementStateStore.getState().setError();
            return;
        }

        this.useAutoPlacementStateStore.getState().initialize();

        try {
            const token = this.fluxAuthService.getFluxToken();
            const req = await fetch(AutoPlacementApi.urlFor("start_job", "v1"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({
                    userUid: user.uid,
                    userHandle: user.handle,
                    projectUid: documentState?.uid,
                }),
            });

            if (!req.ok) {
                throw new Error(`Failed to start auto-placement job: ${req.statusText}`);
            }

            const res = await req.json();
            // eslint-disable-next-line no-console
            console.log("PLACEMENT start job:", res);

            this.subscribeToJobState(res.jobUid);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("PLACEMENT error:", error);
            this.useAutoPlacementStateStore.getState().clear();
            this.showErrorSnackbar("Failed to start auto-placement. Please try again.");
        }

        // const doIteration = async () => {
        //     const token = this.fluxAuthService.getFluxToken();
        //     return fetch(
        //         `${AutoPlacementApi.urlFor("project_job_step", "v1")}?projectUid=${
        //             this.reduxStoreService.getStore().getState().document?.uid
        //         }&userUid=${user.uid}&userHandle=${user.handle}`,
        //         {
        //             headers: {
        //                 ...(token ? {Authorization: `Bearer ${token}`} : {}),
        //             },
        //         },
        //     );
        // };

        // await doIteration();

        // while (await doIteration()) {
        //     // eslint-disable-next-line no-console
        //     console.log("PLACEMENT:", "iteration done");
        // }
    }

    async pause() {
        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        const previousState = this.useAutoPlacementStateStore.getState().autoPlacementState?.state;
        this.useAutoPlacementStateStore.getState().setJobState(AutoLayoutClientJobState.pausing);

        try {
            const token = this.fluxAuthService.getFluxToken();
            const req = await fetch(AutoPlacementApi.urlFor("pause_job", "v1"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({
                    userUid: currentUser.uid,
                    userHandle: currentUser.handle,
                    projectUid: documentUid,
                }),
            });

            if (!req.ok) {
                throw new Error(`Failed to pause auto-placement job: ${req.statusText}`);
            }

            const res = await req.json();
            // eslint-disable-next-line no-console
            console.log("PLACEMENT:", res);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("PLACEMENT error:", error);
            if (previousState) {
                this.useAutoPlacementStateStore.getState().setJobState(previousState);
            }
            this.showErrorSnackbar("Failed to pause auto-placement. Please try again.");
        }
    }

    async resume() {
        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        const previousState = this.useAutoPlacementStateStore.getState().autoPlacementState?.state;
        this.useAutoPlacementStateStore.getState().setJobState(AutoLayoutClientJobState.resuming);

        try {
            const token = this.fluxAuthService.getFluxToken();
            const req = await fetch(AutoPlacementApi.urlFor("resume_job", "v1"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({
                    userUid: currentUser.uid,
                    userHandle: currentUser.handle,
                    projectUid: documentUid,
                }),
            });

            if (!req.ok) {
                throw new Error(`Failed to resume auto-placement job: ${req.statusText}`);
            }

            const res = await req.json();
            // eslint-disable-next-line no-console
            console.log("PLACEMENT:", res);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("PLACEMENT error:", error);
            if (previousState) {
                this.useAutoPlacementStateStore.getState().setJobState(previousState);
            }
            this.showErrorSnackbar("Failed to resume auto-placement. Please try again.");
        }
    }

    async cancel() {
        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        const previousState = this.useAutoPlacementStateStore.getState().autoPlacementState?.state;
        this.useAutoPlacementStateStore.getState().setJobState(AutoLayoutClientJobState.canceling);

        try {
            const token = this.fluxAuthService.getFluxToken();
            const req = await fetch(AutoPlacementApi.urlFor("cancel_job", "v1"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({
                    userUid: currentUser.uid,
                    userHandle: currentUser.handle,
                    projectUid: documentUid,
                }),
            });

            if (!req.ok) {
                throw new Error(`Failed to cancel auto-placement job: ${req.statusText}`);
            }

            const res = await req.json();
            // eslint-disable-next-line no-console
            console.log("PLACEMENT:", res);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("PLACEMENT error:", error);
            if (previousState) {
                this.useAutoPlacementStateStore.getState().setJobState(previousState);
            }
            this.showErrorSnackbar("Failed to cancel auto-placement. Please try again.");
        }
    }

    async applyCurrentIteration() {
        const currentUser = this.currentUserService.getCurrentUser();
        if (!currentUser) return;

        const documentUid = this.documentStore.getState().document?.uid;
        if (!documentUid) return;

        const previousState = this.useAutoPlacementStateStore.getState().autoPlacementState?.state;
        this.useAutoPlacementStateStore.getState().setJobState(AutoLayoutClientJobState.applying);

        try {
            await this.documentPreviewService.applyPreview();

            const token = this.fluxAuthService.getFluxToken();
            const req = await fetch(AutoPlacementApi.urlFor("apply_job", "v1"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({
                    userUid: currentUser.uid,
                    userHandle: currentUser.handle,
                    projectUid: documentUid,
                }),
            });

            if (!req.ok) {
                throw new Error(`Failed to apply auto-placement iteration: ${req.statusText}`);
            }

            await req.json();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("PLACEMENT error:", error);
            if (previousState) {
                this.useAutoPlacementStateStore.getState().setJobState(previousState);
            }
            this.showErrorSnackbar("Failed to apply auto-placement. Please try again.");
        }
    }

    async selectIteration(index: number) {
        const job = this.useAutoPlacementStateStore.getState().autoPlacementState?.currentJob;
        if (!job) return;
        this.useAutoPlacementStateStore.getState().setSliderPosition(index);
        return this.showLatestPreview(job, index);
    }

    private showErrorSnackbar(message: string) {
        this.appStore.getState().enqueueNotification(message, "autoPlacement", {
            variant: "error",
            preventDuplicate: true,
        });
    }
}
